export const PLACE_AFTER_START = 'start'

export const UNTITLED_QUESTION_LABEL = 'Untitled question'

const LABEL_MAX_LENGTH = 42

/**
 * Moves a question from one index to another, returning a new array.
 * Item object references are preserved so callers can remap tracked
 * indexes with `newQuestions.indexOf(trackedQuestion)`.
 * Returns the original array untouched for no-ops (invalid `fromIndex`
 * or a clamped `toIndex` equal to `fromIndex`).
 */
export const moveQuestion = (questions, fromIndex, toIndex) => {
  if (fromIndex < 0 || fromIndex >= questions.length) {
    return questions
  }

  const boundedToIndex = Math.max(0, Math.min(questions.length - 1, toIndex))

  if (boundedToIndex === fromIndex) {
    return questions
  }

  const newQuestions = [...questions]
  const [movedQuestion] = newQuestions.splice(fromIndex, 1)
  newQuestions.splice(boundedToIndex, 0, movedQuestion)

  return newQuestions
}

/**
 * Inserts a question right after `index`, returning a new array.
 * An index of -1 (no current question) inserts at the start.
 */
export const insertQuestionAfter = (questions, index, question) => {
  const insertAt = Math.max(0, Math.min(questions.length, index + 1))
  const newQuestions = [...questions]
  newQuestions.splice(insertAt, 0, question)

  return newQuestions
}

export const isDefaultQuestion = question => !!(question && (question.id || question.objectiveType))

/**
 * Index the "Add New Question" insertion should anchor on: the current
 * question — unless it is a default, in which case the anchor skips to
 * the end of the contiguous run of defaults so the new question never
 * splits the default block (with 4 defaults on screen 1, the new
 * question lands at position 5, as before the reorder feature).
 */
export const getAddQuestionAnchorIndex = (questions, currentIndex) => {
  if (currentIndex < 0 || !isDefaultQuestion(questions[currentIndex])) {
    return currentIndex
  }

  let anchorIndex = currentIndex
  while (anchorIndex + 1 < questions.length && isDefaultQuestion(questions[anchorIndex + 1])) {
    anchorIndex += 1
  }

  return anchorIndex
}

export const questionLabel = (question, maxLength = LABEL_MAX_LENGTH) => {
  const text = ((question && question.question) || '').trim()
  const label = text || UNTITLED_QUESTION_LABEL

  if (label.length <= maxLength) {
    return label
  }

  return `${label.slice(0, maxLength - 1).trimEnd()}…`
}

/**
 * Options for the "Place Question After" control: the start-of-survey
 * option plus every other question labeled with its current position,
 * the question being edited excluded.
 */
export const buildPlaceAfterOptions = (questions, currentIndex) => [
  {value: PLACE_AFTER_START, label: 'Start of the survey (make it first)'},
  ...questions
    .map((question, index) => ({value: index, label: `${index + 1}. ${questionLabel(question)}`}))
    .filter(({value}) => value !== currentIndex),
]

/**
 * Resolves a "Place Question After" selection into the index the moved
 * question must land on, accounting for the moved question leaving its
 * current slot when the anchor sits after it.
 */
export const getPlaceAfterTargetIndex = (currentIndex, anchorValue) => {
  if (anchorValue === PLACE_AFTER_START) {
    return 0
  }

  return anchorValue < currentIndex ? anchorValue + 1 : anchorValue
}

export const getDerivedQuestionKey = question =>
  `${question.id != null ? question.id : ''}|${question.objectiveType}|${question.choiceType}|${
    question.question
  }`

const isDerivedQuestion = question => !!question.objectiveType

/**
 * Position-preserving merge of re-derived default questions into the
 * current array (design D6). Derived rows are matched by id (when
 * present) combined with (objectiveType, choiceType, question) — legal
 * open-ended extras all share the placeholder id 1, so the id alone
 * cannot identify them. Surviving defaults keep their positions but
 * ADOPT the freshly derived object, so re-derived content the key does
 * not capture (e.g. award-answer choices) still propagates; genuinely
 * new defaults are prepended in derived order, stale defaults and user
 * questions rejected by `isUserQuestionAllowed` are dropped.
 */
export const mergeDerivedDefaults = (currentQuestions, derivedDefaults, isUserQuestionAllowed) => {
  const remainingDerived = new Map(
    derivedDefaults.map(question => [getDerivedQuestionKey(question), question]),
  )

  const survivors = []
  currentQuestions.forEach(question => {
    if (!isDerivedQuestion(question)) {
      if (isUserQuestionAllowed(question)) {
        survivors.push(question)
      }
      return
    }

    const key = getDerivedQuestionKey(question)
    const freshQuestion = remainingDerived.get(key)

    if (freshQuestion) {
      remainingDerived.delete(key)
      survivors.push(freshQuestion)
    }
  })

  return [...remainingDerived.values(), ...survivors]
}

const questionKeys = new WeakMap()
let nextQuestionKey = 0

/**
 * Stable client-only key for a question object, lazily assigned. Used
 * as the sortable id in the reorder view; never reaches the submit
 * payload since it lives outside the question object.
 */
export const getQuestionKey = question => {
  if (!questionKeys.has(question)) {
    nextQuestionKey += 1
    questionKeys.set(question, `question-${nextQuestionKey}`)
  }

  return questionKeys.get(question)
}
